import {
    OnInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef, HostListener,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {IrbEditor} from "@irb/irb-editors/irb-editors.base";
import {IEvent, IrbAppEvents, IrbEditorsEvents, IrbModelsEvents} from '@irb-common/events';
import {DataTransferService} from "@irb-common/services/data-transfer.service";
import {
    IModelSecurityEquivalent,
    ICompSecurityDto, ICompSecurityGridItem, IAlternativeSecurity,
} from '@irb/irb-editors/irb-editors.model';
import {HttpErrorResponse} from "@angular/common/http";
import {SecurityAssetCategoryComponent} from "@irb/irb-editors/editor-security/security-asset-category/security-asset-category.component";
import {ComponentSecuritiesComponent} from "@irb/irb-editors/editor-security/component-securities/component-securities.component";
import {TradingMinimumsComponent} from "@irb/irb-editors/editor-security/trading-minimums/trading-minimums.component";
import {SecurityPenaltybarComponent} from "@irb/irb-editors/editor-security/security-penaltybar/security-penaltybar.component";
import {SecurityRestrictionsComponent} from "@irb/irb-editors/editor-security/security-restrictions/security-restrictions.component";
import {SecurityEquivalentsAlternativesComponent} from "@irb/irb-editors/editor-security/security-equivalents-alternatives/security-equivalents-alternatives.component";
import {ExternalShareHoldingComponent} from "@irb/irb-editors/editor-security/external-share-holding/external-share-holding.component";
import {HouseholdLocationPreferencesComponent} from '@irb/irb-editors/editor-security/household-location-preferences/household-location-preferences.component';
import {Observable, of, Subscription, interval} from "rxjs";
import {IAlertDialogData} from "@irb/app.model";
import {SecurityDeleteDTO, SecurityMasterService} from '@irb/irb-models/security-master/security-master.service';
import {IDropdownItem} from '@irb-common/models/dropdown.model';
import {AnalyticsService} from "@irb-common/analytics/analytics.service";
import {
    TlhWashSaleAlternativesComponent
} from "@irb/irb-editors/editor-security/tlh-wash-sale-alternatives/tlh-wash-sale-alternatives.component";
import { AppService } from '@irb/app.service';

@Component({ standalone: false,
    selector: 'irb-editor-security',
    templateUrl: './editor-security.component.html',
    styleUrls: ['./editor-security.component.scss', "../../irb-common/styles/dialog.scss", "../editor-sidebar/editor-sidebar.component.scss"],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.None
})
export class EditorSecurityComponent extends IrbEditor implements OnInit {
    @ViewChild("assetCategoryComponent") assetCategoryComponent: SecurityAssetCategoryComponent;
    @ViewChild("componentSecuritiesComponent") componentSecuritiesComponent: ComponentSecuritiesComponent;
    @ViewChild("tradingMinimumsComponent") tradingMinimumsComponent: TradingMinimumsComponent
    @ViewChild("securityPenaltybarComponent") securityPenaltybarComponent: SecurityPenaltybarComponent;
    @ViewChild("securityRestrictionsComponent") securityRestrictionsComponent: SecurityRestrictionsComponent;
    @ViewChild("securityEquivalentsAlternativesComponent") securityEquivalentsAlternativesComponent: SecurityEquivalentsAlternativesComponent;
    @ViewChild("externalShareHoldingComponent") externalShareHoldingComponent: ExternalShareHoldingComponent;
    @ViewChild("householdLocationPreferencesComponent") householdLocationPreferences: HouseholdLocationPreferencesComponent;
    @ViewChild("tLHWashSaleAlternativesComponent") tLHWashSaleAlternativesComponent: TlhWashSaleAlternativesComponent;

    @ViewChild("body") body: ElementRef;
    regExp = /[`$%\^&*+=\-\[\]\\';{}|\\":<>\?]/g
    _collapseForOpenTriggers: boolean;
    showInLineeditFlagSymbol: boolean;
    tooltipMessageSymbol: any;
    showInLineeditFlagSymbolDesc: boolean;
    tooltipMessageSymbolDesc: any;
    securityEventData: any;
    _tradingMinimumData: { multipleSecurity: boolean, isModelSecurity: boolean, securityData: any[] };
    defaultExpandedPanels: Array<string> = ["ASSET_CLASSES","ASSET_CATEGORY", "COMPONENT_SECURITIES",
        "TRADING_MINIMUMS", "PENALTIES",
        "RESTRICTIONS", "EQUIVALENTS_ALTERNATIVES", "TLH_WASH_SALE_ALTERNATIVES", "HOUSEHOLD_LOCATION_PREFERENCES",
        "EXTERNAL_HOLDINGS"];
    editModel: boolean = false;
    modelName: string;
    targetPercentage: any;
    securitiesToDelete: Array<SecurityDeleteDTO> = [];
    isUiTest = false;
    checkDeleteSelectedSecuritiesSub: Subscription;
    isTlhWsaEnabled: boolean = false;
    ENABLE_AS_SECTYPE: boolean;
    ENABLE_SECURITY_HISTORY: boolean = false;

    constructor(private cdr: ChangeDetectorRef,
                public dataTransferService: DataTransferService,
                private readonly securityMasterService: SecurityMasterService,
                public ats: AnalyticsService,
                public as: AppService) {
        super();
        this.identifier = "securityId";
        this.ENABLE_SECURITY_HISTORY = this.as.getItem('IRB_APP_CONFIG')?.featureFlags?.ENABLE_SECURITY_HISTORY || false;
    }

    ngOnInit() {
        const config = this.as.getItem("IRB_APP_CONFIG");
        
        if(config) {
            this.isTlhWsaEnabled = config.isTlhWsaEnabled;
        }

        this.ENABLE_AS_SECTYPE = this.as.getItem('IRB_APP_CONFIG')?.featureFlags?.ENABLE_AS_SECTYPE || false;
    }

    get(event: IEvent) {
        super.get(event);
        this.ats.trackSecuritiesEditEvent();
        // this.isLoading = false;
        this.securityEventData = event.data.data;
        this.editModel = event.data.editModel || false;
        this.modelName = event.data.modelName || '';
        if (this.editModel && this.securityEventData) {
            const trgt = this.securityEventData.targetPercentage ? this.securityEventData.targetPercentage : 0;
            this.targetPercentage = Number(trgt).toFixed(1)
        }

        // this.data = event.data.data;
        this.defaultExpandedPanels = this.setExpanPanelList(event);
        this.initExpansionPanels(event);
        if (this.editModel) {
            this.tooltipMessageSymbol = "";
            this.showInLineeditFlagSymbol = false;
            this.tooltipMessageSymbolDesc = "";
            this.showInLineeditFlagSymbolDesc = false;
            this.load(this.securityEventData);
            this.isLoading = false;
        } else {
            this.ies.getSecurityDetail(this.securityEventData.securityId).subscribe((data: any) => {
                this.tooltipMessageSymbol = "";
                this.showInLineeditFlagSymbol = false;
                this.tooltipMessageSymbolDesc = "";
                this.showInLineeditFlagSymbolDesc = false;
                this.securitiesToDelete = [];

                this.load(data);
                this.isLoading = false;
            });
        }
    }

    setExpanPanelList(event: IEvent) {
        const expandedPanels = ["ASSET_CLASSES","ASSET_CATEGORY", "COMPONENT_SECURITIES",
            "TRADING_MINIMUMS", "PENALTIES",
            "RESTRICTIONS", "EQUIVALENTS_ALTERNATIVES", "TLH_WASH_SALE_ALTERNATIVES", "HOUSEHOLD_LOCATION_PREFERENCES",
            "EXTERNAL_HOLDINGS"];
        let panels: string[] = [];
        let securityDaata = event.data.data;
        let editModel = event.data.editModel || false;

        if (event.data.data) {
            expandedPanels.forEach((componentId: string) => {
                switch (componentId) {
                    case 'ASSET_CATEGORY':
                        if (!editModel) {
                            panels.push(componentId);
                        }
                        break;
                    case 'COMPONENT_SECURITIES':
                        if (!editModel &&
                            this.formatSecurityType(securityDaata.securityType) === 'Group') {
                            panels.push(componentId);
                        }
                        break;
                    case 'PENALTIES':
                        if (!editModel &&
                            this.formatSecurityType(securityDaata.securityType) === 'Mutual Fund') {
                            panels.push(componentId);
                        }
                        break;
                    case 'EXTERNAL_HOLDINGS':
                        if (!editModel && this.formatSecurityType(securityDaata.securityType) === 'External' &&
                            this.dataTransferService.ShowExternalAccounts) {
                            panels.push(componentId);
                        }
                        break;
                    default:
                        panels.push(componentId);
                        break;
                }
            });
        } else {
            panels = expandedPanels;
        }

        return panels;
    }

    load(data: any) {
        super.load(data);
        let isModelSecurity = this.editModel;
        this.setTradeMinimumData(data, isModelSecurity);
        this.cdr.detectChanges();
        setTimeout(() => this.onResize());
    }

    private setTradeMinimumData(data: any, isModelSecurity: boolean) {
        this._tradingMinimumData = {multipleSecurity: false, isModelSecurity: isModelSecurity, securityData: [data]};
    }

    initExpansionPanels(event: IEvent) {
        // const securityData = event.data.data;
        let assetCategoryTabInit = event.data.assetCategoryInit || false;
        let componentSecuritiesTabInit = event.data.componentSecuritiesInit || false;
        let tradingMinimumsTabInit = event.data.tradingMinimumsInit || false;
        let penaltiesTabInit = event.data.penaltiesInit || false;
        let restrictionsTabInit = event.data.restrictionsInit || false;
        let equivalentsTabInit = event.data.equivalentsInit || false;
        let houseHoldLocationPrefTabInit = event.data.houseHoldLocationPrefInit || false;
        let externalSharesTabInit = event.data.externalSharesInit || false;
        let tlhWsaInit = event.data.tlhWsaInit || false;
        this._collapseForOpenTriggers = (assetCategoryTabInit || componentSecuritiesTabInit || tradingMinimumsTabInit || penaltiesTabInit
            || restrictionsTabInit || equivalentsTabInit || tlhWsaInit || houseHoldLocationPrefTabInit || externalSharesTabInit) ? true : false;

        let dep = JSON.parse(JSON.stringify(this.defaultExpandedPanels));
        let sessionOverridesDefault = true;
        if (assetCategoryTabInit) {
            dep = ["ASSET_CATEGORY"];
            sessionOverridesDefault = false;
        } else if (componentSecuritiesTabInit) {
            dep = ["COMPONENT_SECURITIES"];
            sessionOverridesDefault = false;
        } else if (tradingMinimumsTabInit) {
            dep = ["TRADING_MINIMUMS"];
            sessionOverridesDefault = false;
        } else if (penaltiesTabInit) {
            dep = ["PENALTIES"];
            sessionOverridesDefault = false;
        } else if (restrictionsTabInit) {
            dep = ["RESTRICTIONS"];
            sessionOverridesDefault = false;
        } else if (equivalentsTabInit) {
            dep = ["EQUIVALENTS_ALTERNATIVES"];
            sessionOverridesDefault = false;
        } else if (houseHoldLocationPrefTabInit) {
            dep = ["HOUSEHOLD_LOCATION_PREFERENCES"];
            sessionOverridesDefault = false;
        } else if (externalSharesTabInit) {
            dep = ["EXTERNAL_HOLDINGS"];
            sessionOverridesDefault = false;
        } else if (tlhWsaInit) {
            dep = ["TLH_WASH_SALE_ALTERNATIVES"];
            sessionOverridesDefault = false;
        }
        // modify if the targeted panel need to be expanded and all other needs to be collapsed.

        if (!this.isUiTest && this.ENABLE_SECURITY_HISTORY)
            this.defaultExpandedPanels.push("SECURITY_HISTORY");
        this.ies.loadExpansionState(this.defaultExpandedPanels.length, 'editor-security', dep, sessionOverridesDefault);
    }


    get collapseForOpenTriggers() {
        return this._collapseForOpenTriggers;
    }

    formatSecurityType(securityType: any) {
        switch (securityType) {
            case "EQUITY":
                return "Equity";
            case "EQ":
                return "Equity";
            case "MUTUAL_FUND":
                return "Mutual Fund";
            case "MF":
                return "Mutual Fund";
            case "CASH":
                return "Cash";
            case "GROUP":
                return "Group";
            case "BOND":
                return "Bond";
            case "OTHER":
                return "Other";
            case "EXTERNAL":
                return "External";
            case "CustCat":
                return "Category";
            case "OPTION":
                return "Option";
            default:
                return securityType;
        }
    }

    @HostListener("window:resize", ["$event"])
    onResize() {
        if (this.body) {
            const rect = this.body.nativeElement.getBoundingClientRect();
            let h = (window.innerHeight - rect.top - 10);

            h = (h < 200) ? 200 : h;
            this.bodyHeight = h + "px";
        }
    }

    showInlineEditSymbol() {
        this.showInLineeditFlagSymbol = true;
        this.showInLineeditFlagSymbolDesc = false;
        this.cdr.detectChanges();
    }

    saveInlineEditSymbol(symbolName: any) {
        if (symbolName.trim() === '') {
            this.closeInlineEditSymbol();
            return;
        }
        this.data.securitySymbol = symbolName;
        this.showInLineeditFlagSymbol = false;
        this.onInputChange();
    }

    closeInlineEditSymbol() {
        this.tooltipMessageSymbol = "";
        this.data.securitySymbol = super.getOriginalData().securitySymbol;
        this.onInputChange();
        this.showInLineeditFlagSymbol = false;
        this.cdr.detectChanges();
    }

    onSymbolTextChange() {
        this.tooltipMessageSymbol = "";
    }

    showInlineEditSymbolDesc() {
        this.showInLineeditFlagSymbolDesc = true;
        this.showInLineeditFlagSymbol = false;
        this.cdr.detectChanges();
    }

    saveInlineEditSymbolDesc(securityDescription: any) {
        this.data.securityDescription = securityDescription;
        this.showInLineeditFlagSymbolDesc = false;
        this.onInputChange();
    }

    closeInlineEditSymbolDesc() {
        this.tooltipMessageSymbolDesc = "";
        this.data.securityDescription = super.getOriginalData().securityDescription;
        this.onInputChange();
        this.showInLineeditFlagSymbolDesc = false;
        this.cdr.detectChanges();
    }

    onSymbolDescTextChange() {
        this.tooltipMessageSymbolDesc = "";
    }

    isAllowedToEditHeader() {
        let isAllowed = false;
        if (this.data && this.data.securityType && !this.editModel) {
            isAllowed = (this.data.securityType.toUpperCase() === 'SMA'
                || this.data.securityType.toUpperCase() === 'OTHER'
                || this.data.securityType.toUpperCase() === 'GROUP');
        }
        return isAllowed;
    }

    onCustomAssetCategoryChanged(selectedCustomCategory: IDropdownItem) {
        this.data.customCategoryName = selectedCustomCategory.name;
        this.data.customCategoryId = selectedCustomCategory.value;
        this.onInputChange();
    }

    onTradingMinimumsChanged(event: { key: string, data: any }) {
        this.data[event.key] = event.data;
        this.onInputChange();
        this.setFundImposedMinimums();
    }

    onTradingMinimumAlternateSecurityChanged(event: IDropdownItem) {
        if (Number(event.value) === 0) {
            this.data.tradeMinimumAlternative = null;
            // for Model request
            if (this.editModel) {
                this.data.tradeMinAltName = null;
            } else {
                this.data.tradeMinimumAlternativeSymb = null;
            }
        } else {
            this.data.tradeMinimumAlternative = Number(event.value);
            // for Model request
            if (this.editModel) {
                this.data.tradeMinAltName = event.name;
            } else {
                this.data.tradeMinimumAlternativeSymb = event.name;
            }
        }

        this.onInputChange();
    }

    onGroupSecurityChanged(event: Array<ICompSecurityDto>) {
        if (event.length > 0) {
            this.data.groupCompSecurityDtoSet = event;
            this.data.isGrpCompEdited = true;
            // below property needs to be populated when items are deleted.
            // this.data["deletedSecIdList"] = deletedSecProfileIdArr;
            this.onInputChange();
        } else {
            this.data.groupCompSecurityDtoSet = this.getOriginalData().groupCompSecurityDtoSet;
            this.data.isGrpCompEdited = false;
            this.onInputChange();
        }
    }

    onDeletedSecIdListChanged(event: Array<ICompSecurityGridItem>) {
        const securityDetails = event;
        if (securityDetails && securityDetails.length > 0) {
            securityDetails.forEach((securityGridItem) => {
                const itemToDelete = {
                    securityId: securityGridItem.securityId,
                    versionId: securityGridItem.versionId
                };
                this.securitiesToDelete.push(itemToDelete);
            });
            this.data.deletedSecIdList = [];
            this.onInputChange();
        }
    }

    onExternalSharePriceChange(event: any) {
        this.data.externalSharePrice = event;
        this.onInputChange();
    }

    onSecurityRestrictionChanged(event: { sellingTreatment: any, conditions: any, altSecurity: IDropdownItem }) {
        this.data.sellingTreatment = event.sellingTreatment;
        this.data.conditions = event.conditions;

        if (event.altSecurity) {
            this.data.altSecurityId = event.altSecurity.value;
            this.data.altSecuritySymbol = event.altSecurity.name;
        } else {
            this.data.altSecurityId = null;
            this.data.altSecuritySymbol = null;
        }

        this.onInputChange();
    }

    showExternalAccountsPanel() {
        let showExternalAccounts;
        showExternalAccounts = (this.data && this.data.securityType &&
            (this.formatSecurityType(this.data.securityType) === "External") && this.dataTransferService.ShowExternalAccounts);
        return showExternalAccounts;
    }

    onRothLevelChanged(event: Number) {
        this.data.rothLevel = event;
        this.onInputChange();
    }

    onLevelChanged(event: Number) {
        this.data.taxabilityBias = event;
        this.onInputChange();
    }

    onSecurityEquivalentsChanged(secEquivalents: Array<IModelSecurityEquivalent>) {
        // console.log('Security Equivalents editor security', secEquivalents);
        this.data.secEquivalentsDtoSet = secEquivalents;
        const isModified = !this.util.compare(this.originalData.secEquivalentsDtoSet, this.data.secEquivalentsDtoSet);
        this.data.isSecEqvsEdited = isModified;
        this.onInputChange();
    }

    onSecurityEquivalentsAlternativeChanged(event: { alternativeSecurity: IAlternativeSecurity, inverseAccountType: string }) {
        if (event.alternativeSecurity && event.alternativeSecurity.securityId > 0) {
            if (this.editModel) {
                this.data.taxAltSymbolName = event.alternativeSecurity.securitySymbol;
            } else {
                this.data.taxQualAltSecSymb = event.alternativeSecurity.securitySymbol;
            }

            const altSymbol = (event.inverseAccountType === "QUALIFIED" ? "qualifiedAlternativeSymbol" : "taxableAlternativeSymbol");
            const negatedAltSymbol = (event.inverseAccountType === "QUALIFIED" ? "taxableAlternativeSymbol" : "qualifiedAlternativeSymbol");
            this.data[altSymbol] = event.alternativeSecurity.securityId;
            this.data[negatedAltSymbol] = null;

            if (this.editModel) {
                this.data.inverseAccType = event.inverseAccountType;
            } else {
                this.data.invrsAccountType = event.inverseAccountType;
            }
        } else {
            if (this.editModel) {
                this.data.taxAltSymbolName = null;
            } else {
                this.data.taxQualAltSecSymb = null;
            }
            this.data.qualifiedAlternativeSymbol = null;
            this.data.taxableAlternativeSymbol = null;
            if (this.editModel) {
                this.data.inverseAccType = null;
            } else {
                this.data.invrsAccountType = null;
            }
        }
        this.onInputChange();
    }

    isVisible(componentId: string) {
        if (!this._isLoading && this._tradingMinimumData && this._tradingMinimumData.securityData) {
            switch (componentId) {
                case 'ASSET_CATEGORY':
                    return !this.editModel;
                case 'COMPONENT_SECURITIES':
                    return (!this.editModel &&
                        this.formatSecurityType(this._tradingMinimumData.securityData[0].securityType) === 'Group');
                case 'TRADING_MINIMUMS':
                    return true;
                case 'PENALTIES':
                    return (!this.editModel &&
                        this.formatSecurityType(this._tradingMinimumData.securityData[0].securityType) === 'Mutual Fund');
                case 'RESTRICTIONS':
                    return true;
                case 'EQUIVALENTS_ALTERNATIVES':
                    return true;
                case 'TLH_WASH_SALE_ALTERNATIVES':
                    return true;
                case 'HOUSEHOLD_LOCATION_PREFERENCES':
                    return true;
                case 'EXTERNAL_HOLDINGS':
                    return (!this.editModel && this.formatSecurityType(this._tradingMinimumData.securityData[0].securityType) === 'External' &&
                        this.dataTransferService.ShowExternalAccounts);
            }
        } else {
            return false;
        }

    }

    isCashSecurityProfile() {
        if (this.securityEventData && this.securityEventData.securitySymbol === "$CASH") {
            return true;
        } else {
            return false;
        }
    }

    save() {
        this.validateInput().subscribe(errors => {
            if (errors.length > 0) {
                let errMsg: string = errors.join("\n");
                this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
                    dTitle: "Alert",
                    msg: errMsg,
                    dProceedBtnLabel: "Ok",
                    dType: "ERROR"
                } as IAlertDialogData);
            } else {
                let securityToSave = this.getSecurityDataForSave();
                    
                if (this.editModel) {
                    // save for the edit model security screen.
                    const params = {
                        editModel: true,
                        security: securityToSave
                    };
                    this.as.fireEvent(IrbModelsEvents.SAVE_EDIT_SECURITY, params);
                    this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot});
                    this.hasPendingFunction = false;
                } else {
                    // save for the edit security screen.
                    this.as.fireEvent(IrbEditorsEvents.SAVE_START, {"slot": this.slot});
                    if (this.securitiesToDelete && this.securitiesToDelete.length > 0) {
                        this.securityMasterService.deleteSelectedSecurities(this.securitiesToDelete).subscribe((res: any) => {
                            if(res.responseUtilityMap?.statusId) {
                                this.checkDeleteSelectedSecuritiesStatus(res.responseUtilityMap.statusId);
                            }
                        });
                    } else {
                        this.saveSecurityProfile(securityToSave);
                    }
                }
            }
        });
    }

    checkDeleteSelectedSecuritiesStatus(statusId: string) {
        this.checkDeleteSelectedSecuritiesSub = interval(3000).subscribe(() => {
            this.securityMasterService.getDeleteSelectedModelStatus(statusId).subscribe((res: any) => {
                if(res.responseStatus.toUpperCase() === "SUCCESS" && res.responseUtilityMap.requestStatus.toUpperCase() === "COMPLETED") {
                    this.securitiesToDelete = [];
                    this.checkDeleteSelectedSecuritiesSub.unsubscribe();
                    this.completeDeleteSelectedSecurities(res);
                } else if (res.responseStatus.toUpperCase() === "STALEDATAEXCEPTION") {
                    this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
                    this.securitiesToDelete = [];
                    this.checkDeleteSelectedSecuritiesSub.unsubscribe();
                    this.fireStaleDataExceptionHandler();
                } else if (res.responseStatus.toUpperCase() === "EXCEPTION") {
                    this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
                    this.securitiesToDelete = [];
                    this.checkDeleteSelectedSecuritiesSub.unsubscribe();
                    this.fireExceptionHandler();
                                
                    if (!this.isUiTest) {
                        throw new Error('Editor Security deleteSelectedSecurities failed. securityId=' + this.securityEventData.securityId);
                    }
                }
            });
        });
    }

    completeDeleteSelectedSecurities(_res: any) {
        this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});

        const dtlMsg = _res.responseData === 1 ? "1 security deleted." : _res.responseData + " securities deleted.";
        this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
            severity: 'warn',
            summary: "", 
            detail: dtlMsg
        });

        this.saveSecurityProfile(this.getSecurityDataForSave());
    }

    private saveSecurityProfile(securityToSave: any) {
        this.ies.saveSecurityProfile(securityToSave).subscribe((response: any) => {
            if (response.responseStatus === "SUCCESS") {
                if (response.responseData != null) {
                    let data = response.responseData;
                    this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, {
                        "hasPendingFunction": this.hasPendingFunction,
                        "data": "Security updated.",
                        "slot": this.slot
                    });
                    this.tooltipMessageSymbol = "";
                    this.showInLineeditFlagSymbol = false;
                    this.tooltipMessageSymbolDesc = "";
                    this.showInLineeditFlagSymbolDesc = false;
                   //  this.load(data);
                }
                this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot})
                this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
            } else if (response.responseStatus === "BUSINESS_ERR_MSG" || response.responseStatus === "EXCEPTION") {
                this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
                if (response.responseStatus === "BUSINESS_ERR_MSG" && response.responseUtilityMap && response.responseUtilityMap.Message) {
                    this.fireSymbolAlreadyExistsExceptionHandler();
                    if (!this.isUiTest)
                        throw new Error('Editor Security saveSecurityProfile failed. securityId=' + this.securityEventData.securityId);
                } else {
                    this.fireExceptionHandler();
                    if (!this.isUiTest)
                        throw new Error('Editor Security saveSecurityProfile failed. securityId=' + this.securityEventData.securityId);
                }
            } else if (response.responseStatus === 'STALEDATAEXCEPTION') {
                this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT);
                this.fireStaleDataExceptionHandler();
            } else {
                this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {'slot': this.slot});
                this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot});
                this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
                if (!this.isUiTest)
                    throw new Error('Editor Security saveSecurityProfile failed. securityId=' + this.securityEventData.securityId);
            }
        }, (_err: HttpErrorResponse) => {
            this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {'slot': this.slot});
            this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot});
            this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
            if (!this.isUiTest)
                throw new Error('Editor Security saveSecurityProfile failed. securityId=' + this.securityEventData.securityId);
        }, () => {

        });
    }

    private fireStaleDataExceptionHandler() {
        this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
            dTitle: 'Refresh Required',
            dProceedBtnLabel: 'Refresh',
            msg: "You are attempting to edit a security that was just modified by someone else. To see the updated security, you need to refresh the page.",
            onProceed: () => {
                this.isLoading = true;
                this.ies.getSecurityDetail(this.securityEventData.securityId).subscribe((data: any) => {
                    this.load(data);
                    this.isLoading = false;
                    this.cdr.detectChanges();
                });
            },
            onCancel: () => {
                this.revert();
            }

        } as IAlertDialogData);
    }

    private fireExceptionHandler() {
        this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
            severity: "error",
            summary: "Please refresh Page",
            detail: "Exception occurred",
        });
    }

    private fireSymbolAlreadyExistsExceptionHandler() {
        this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
            severity: "warn",
            summary: "",
            detail: "Security already exists, please choose another security name.",
        });
    }

    checkIfValidCustomSymbol(unRecognizedSymbol: any): boolean {
        let notAllowedSpCharSymbol = "", allowedSpCharSymbol = "";
        const notAllowedSpCharPattern = /[^~!./#a-zA-Z0-9]/g;
        const allowedSpCharPattern = /[~!./#]/g;
        
        if (unRecognizedSymbol.match(notAllowedSpCharPattern) && unRecognizedSymbol.match(notAllowedSpCharPattern) != null) {
            notAllowedSpCharSymbol = unRecognizedSymbol.match(notAllowedSpCharPattern);
        }

        if (unRecognizedSymbol.match(allowedSpCharPattern) && unRecognizedSymbol.match(allowedSpCharPattern) != null) {
            allowedSpCharSymbol = unRecognizedSymbol.match(allowedSpCharPattern);
        }

        if ((notAllowedSpCharSymbol && notAllowedSpCharSymbol.length > 0) || (allowedSpCharSymbol && allowedSpCharSymbol.length > 2)) {
            return false;
        } else {
            return true;
        }
    }

    getSecurityDataForSave() {
        let saveObject = this.util.clone(this.data);
        let isMinBuyChanged = false, isMinSelChanged = false, isMinBuyPercentChanged = false,
            isMinSelPercentChanged = false;
        let advisorImposedBuyMinVal = this.dataTransferService.advisorImposedBuyMin ? this.dataTransferService.advisorImposedBuyMin : 0;
        let advisorImposedSellMinVal = this.dataTransferService.advisorImposedSellMin ? this.dataTransferService.advisorImposedSellMin : 0;
        let advisorImposedBuyPerMinVal = this.dataTransferService.advisorImposedBuyMinPercent ? this.dataTransferService.advisorImposedBuyMinPercent : 0;
        let advisorImposedSellPerMinVal = this.dataTransferService.advisorImposedSellMinPercent ? this.dataTransferService.advisorImposedSellMinPercent : 0;

        saveObject["isInlineEdit"] = 0;
        // set minChanged Flags.
        if (saveObject['buyMinimum'] !== advisorImposedBuyMinVal) {
            isMinBuyChanged = true;
        }
        if (saveObject['sellMinimum'] !== advisorImposedSellMinVal) {
            isMinSelChanged = true;
        }
        if (saveObject['buyMinimumPercent'] !== advisorImposedBuyPerMinVal) {
            isMinBuyPercentChanged = true;
        }
        if (saveObject['sellMinimumPercent'] !== advisorImposedSellPerMinVal) {
            isMinSelPercentChanged = true;
        }
        saveObject['isMinBuyChanged'] = isMinBuyChanged;
        saveObject['isMinSelChanged'] = isMinSelChanged;
        saveObject['isMinBuyPercentChanged'] = isMinBuyPercentChanged;
        saveObject['isMinSelPercentChanged'] = isMinSelPercentChanged;

        //this.data.qualifiedAlternativeSymbol = null;
        //this.data.taxableAlternativeSymbol = null;
        if(!saveObject.qualifiedAlternativeSymbol) {
            saveObject.qualifiedAlternativeSymbol = 0;
        }
        if(!saveObject.taxableAlternativeSymbol) {
            saveObject.taxableAlternativeSymbol = 0;
        }
        if(!saveObject.tradeMinimumAlternative) {
            saveObject.tradeMinimumAlternative = 0;
        }

        if(saveObject.taxableAlternativeSymbol  && saveObject.taxableAlternativeSymbol > 0) {
            saveObject.qualifiedAlternativeSymbol = null;
        }
        if(saveObject.qualifiedAlternativeSymbol  && saveObject.qualifiedAlternativeSymbol > 0) {
            saveObject.taxableAlternativeSymbol = null;
        }

        if (saveObject.securitySymbol){
            saveObject.securitySymbol = saveObject.securitySymbol.toUpperCase();
        }

        return saveObject;
    }

    setFundImposedMinimums() {
        // this is valid only for mutual fund types.
        if (this.formatSecurityType(this.data.securityType) === 'Mutual Fund') {
            if (this.data.isFundImposedMinOverridden) {
                this.data["qualInitialPurchase"] = 0;
                this.data["qualSubsPurchase"] = 0;
                this.data["taxInitialPurchase"] = 0;
                this.data["taxSubsPurchase"] = 0;
                this.cdr.detectChanges();
            } else {
                this.securityMasterService.getMutualFundSecurityDetails(this.data.securityId).subscribe((data: any) => {
                    this.data["qualInitialPurchase"] = data.qualInitialPurchase;
                    this.data["qualSubsPurchase"] = data.qualSubsPurchase;
                    this.data["taxInitialPurchase"] = data.taxInitialPurchase;
                    this.data["taxSubsPurchase"] = data.taxSubsPurchase;
                    this.cdr.detectChanges();
                });
            }
        } 
    }

    areValidAllComps() {
        let valid = true;
        if (this.isVisible('ASSET_CATEGORY') && this.assetCategoryComponent) {
            valid = valid && this.assetCategoryComponent.isValid();
        }
        if (this.isVisible('COMPONENT_SECURITIES') && this.componentSecuritiesComponent) {
            valid = valid && this.componentSecuritiesComponent.isValid();
        }

        if (this.isVisible('TRADING_MINIMUMS') && this.tradingMinimumsComponent) {
            valid = valid && this.tradingMinimumsComponent.isValid();
        }

        if (this.isVisible('PENALTIES') && this.securityPenaltybarComponent) {
            valid = valid && this.securityPenaltybarComponent.isValid();
        }

        if (this.isVisible('RESTRICTIONS') && this.securityRestrictionsComponent) {
            valid = valid && this.securityRestrictionsComponent.isValid();
        }

        if (this.isVisible('EQUIVALENTS_ALTERNATIVES') && this.securityEquivalentsAlternativesComponent) {
            valid = valid && this.securityEquivalentsAlternativesComponent.isValid();
        }

        if (this.isVisible('TLH_WASH_SALE_ALTERNATIVES') && this.tLHWashSaleAlternativesComponent) {
            valid = valid && this.tLHWashSaleAlternativesComponent.isValid();
        }

        if (this.isVisible('EXTERNAL_HOLDINGS') && this.externalShareHoldingComponent) {
            valid = valid && this.externalShareHoldingComponent.isValid();
        }

        if (this.isVisible('HOUSEHOLD_LOCATION_PREFERENCES') && this.householdLocationPreferences) {
            valid = valid && this.householdLocationPreferences.isValid();
        }
        return valid;
    }

    validateInput(): Observable<string[]> {
        const errors: string[] = [];
        if (!this.areValidAllComps()) {
            errors.push('Security cannot be saved. Please complete all the required fields.')
        }
        return of(errors);
    }

    onSecurityTLHWashSaleEquivalentsChanged(event: any) {
        this.data.isTlhEnabled = event.isTlhEnabled;
        this.data.tlhWashSaleAltSecuritiesDTO = event.tlhWashSaleAltSecuritiesDTO;
        if (this.editModel) {
            this.data.tlhWashSaleSymbol = event.tlhWashSaleSymbol;
        }
        this.onInputChange();
        this.isValid = event.isValid;
    }

}
